<template>
  <div class="stepper">
    <div class="stepper-nav">
      <ul class="stepper-nav__list">
        <li
          :class="{
            'stepper-nav__item': true,
            'stepper-nav__item--prev': currentStep > i,
            'stepper-nav__item--current': currentStep === i,
            'stepper-nav__item--next': currentStep > i,
          }"
          v-for="({ label, id }, i) in steps"
          :key="id"
          @click="handleStepClicked(id)"
        >
          <img :src="getIcon(i)" alt="current step" />
          <div class="stepper-nav__title-wrapper">
            <span class="stepper-nav__title">{{ label }}</span>
            <p>
              {{ `${$t('estimate.step')} ${i + 1}`}}
              <span class="stepper-nav__number-of-steps">{{ ` / ${steps.length}`}}</span>
            </p>
          </div>
        </li>
      </ul>
    </div>
    <div class="stepper-content">
      <div class="stepper-content__form">
        <component
          :is="steps[currentStep].component"
          :step="steps[currentStep]"
          :stepNumber="currentStep + 1"
          v-on="steps[currentStep].listeners"
        />
      </div>
    </div>
    <div class="stepper-footer">
      <Button
        class="stepper-footer__button stepper-footer__button--prev"
        v-if="!isFirstStep"
        @click="prev"
        :disabled="nextStepLoading"
      >
        <img src="../assets/icons/arrow--dark.svg" alt="" />
        {{ $t('common.prev') }}
      </Button>
      <Button
        class="stepper-footer__button stepper-footer__button--next"
        v-if="!isLastStep"
        @click="next"
        :loading="nextStepLoading"
      >
        {{ $t('common.next') }}
        <img src="../assets/icons/arrow--white.svg" alt="" />
      </Button>
      <slot name="footer" />
    </div>
  </div>
</template>

<script>
import { Button } from '@/components/common';
import StepPrev from '@/assets/icons/step-prev.svg';
import StepCurrent from '@/assets/icons/step-current.svg';
import StepNext from '@/assets/icons/step-next.svg';

export default {
  name: 'Stepper',
  components: { Button },
  props: {
    steps: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      currentStep: 0,
      nextStepLoading: false,
    };
  },
  methods: {
    handleStepClicked(id) {
      // We only enable this in development, for ease of testing
      if (process.env.NODE_ENV === 'development') {
        this.currentStep = this.steps.findIndex(step => step.id === id);
      }
    },
    emitStepChanged() {
      window.scrollTo(0, 0);
      this.$emit('stepChanged', this.steps[this.currentStep].id);
    },
    async next() {
      if (!this.isLastStep) {
        const current = this.steps[this.currentStep];
        if (current.beforeNextStep) {
          try {
            this.nextStepLoading = true;
            await current.beforeNextStep();
            this.currentStep += 1;
            this.emitStepChanged();
          } catch (error) {
            this.$notifications.error(error.message);
          } finally {
            this.nextStepLoading = false;
          }
        } else {
          this.currentStep += 1;
          this.emitStepChanged();
        }
      }
    },
    prev() {
      if (!this.isFirstStep) this.currentStep -= 1;
      this.emitStepChanged();
    },
    getIcon(i) {
      if (this.currentStep > i) return StepPrev;
      if (this.currentStep === i) return StepCurrent;
      return StepNext;
    },
  },
  computed: {
    isLastStep() {
      return this.currentStep >= this.steps.length - 1;
    },
    isFirstStep() {
      return this.currentStep < 1;
    },
    progress() {
      const width = (this.currentStep + 1) / this.steps.length * 100;
      return `${width}%`;
    },
  },
};
</script>

<style lang="scss" scoped>
  .stepper-nav {
    background-color: $stepperBackground;
    border-top-left-radius: 18px;
    border-top-right-radius: 18px;
    height: 124px;
    position: relative;
    width: 100%;

    @include md-up {
      border-bottom: 1px solid $grayLight;
    }

    &__list {
      align-items: center;
      display: flex;
      height: 100%;
      justify-content: space-between;
      padding: 0;
      width: 100%;
    }

    &__item {
      @include center-content();
      display: none;
      flex-grow: 1;
      height: 100%;
      position: relative;

      &::after {
        content: '';
        background-color: $accent;
        bottom: 0;
        display: block;
        height: 2px;
        left: 0;
        position: absolute;
        width: 0;
      }

      @include md-up {
        display: flex;
      }

      img {
        height: 40px;
        margin-right: 24px;
        width: 40px;
      }

      &--prev,
      &--current {
        .stepper-nav__title {
          color: $primary;
        }
      }

      &--current {
        display: flex;

        &.stepper-nav__item {
          justify-content: flex-start;
          padding: 20px;

          @include md-up {
            padding: 0;
            justify-content: center
          }
        }
      }

      &--prev {
        &::after {
          width: 100%;
        }
      }

      &--current {
        &::after {
          transition: width 0.3s ease;
          width: 100%;
        }
      }
    }

    &__title-wrapper {
      color: $secondary;
      display: flex;
      flex-direction: column;
      font-size: 11px;
      font-weight: 500;
      height: 40px;
      justify-content: space-between;
      letter-spacing: .15px;
    }

    &__title {
      font-size: 14px;
      font-weight: 600;
      letter-spacing: .19px;
      white-space: nowrap;
    }

    &__number-of-steps {
      @include md-up {
        display: none;
      }
    }
  }

  .stepper-content {
    align-items: center;
    background-color: $white;
    border-bottom: 1px solid $grayLight;
    display: flex;
    flex-flow: column nowrap;
    padding: 12px 20px 16px;

    @include md-up {
      padding: 60px 40px 80px;
    }

    &__form {
      width: 100%;

      @include md-up {
        width: 586px;
      }
    }
  }

  .stepper-footer {
    @include center-content();
    background-color: $stepperBackground;
    border-bottom-left-radius: 18px;
    border-bottom-right-radius: 18px;
    height: 134px;

    &__button {
      @include center-content();
      font-size: 15px;
      font-weight: 600;
      height: auto;
      letter-spacing: .3px;
      padding: 18px 32px;

      &:not(:first-child) {
        margin-left: 40px;
      }

      &--prev {
        background-color: $white;
        border: 1px solid $grayLight;
        color: $primary;

        &:hover {
          background-color: darken($white, 3%);
          transition: all .6s;
        }

        img {
          margin-right: 16px;
        }
      }

      &--next {
        background-color: $accent;
        color: $white;

        &:hover {
          background-color: darken($accent, 10%);
          transition: all .6s;
        }

        img {
          margin-left: 16px;
        }
      }
    }
  }
</style>
